import React, { ReactNode } from 'react';
import clsx from 'clsx';
import { type HTMLMotionProps, motion, useReducedMotion } from 'motion/react';
import LoadingIndicator from 'components/ui/loading/LoadingIndicator';
import { dashboardTransitions } from 'lib/motion/dashboardMotion';

type Variant =
  | 'default'
  | 'destructive'
  | 'outline'
  | 'secondary'
  | 'ghost'
  | 'link'
  | 'primary'
  | 'danger'
  | 'cancel'
  | 'submit'
  | 'outline';
type Size = 'default' | 'sm' | 'lg' | 'icon';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  loadingLabel?: string;
  asChild?: boolean;
  layoutId?: string;
}

const baseStyles =
  'relative inline-flex transform-gpu items-center justify-center gap-2 whitespace-nowrap rounded-lg border text-sm font-medium ' +
  'transition-colors disabled:pointer-events-none disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400 ' +
  'outline-none focus-visible:ring-2 focus-visible:ring-proposio-purple/45 focus-visible:ring-offset-2';

const variantStyles: Record<Variant, string> = {
  default:
    'border-transparent bg-gradient-to-r from-indigo-600/90 to-purple-600/90 text-white shadow-sm hover:from-indigo-600 hover:to-purple-600',
  primary:
    'border-transparent bg-gradient-to-r from-indigo-600/90 to-purple-600/90 text-white shadow-sm hover:from-indigo-600 hover:to-purple-600',
  submit:
    'w-full border-transparent bg-gradient-to-r from-indigo-600/90 to-purple-600/90 text-white shadow-sm hover:from-indigo-600 hover:to-purple-600',
  destructive: 'border-transparent bg-red-600 text-white shadow-sm hover:bg-red-700',
  danger: 'border-transparent bg-red-600 text-white shadow-sm hover:bg-red-700',
  secondary: 'border-gray-200 bg-white text-gray-900 hover:bg-gray-50',
  cancel: 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50',
  outline: 'border-gray-200 bg-white text-gray-900 hover:bg-gray-50',
  ghost: 'border-transparent bg-transparent text-gray-700 hover:bg-gray-100 hover:text-gray-900',
  link: 'text-proposio-purple underline-offset-4 hover:underline',
};

const sizeStyles: Record<Size, string> = {
  default: 'h-9 px-4 py-2',
  sm: 'h-8 px-3 text-xs',
  lg: 'h-10 px-6',
  icon: 'h-9 w-9 p-0',
};

type ButtonVariantsProps = {
  variant?: Variant;
  size?: Size;
  className?: string;
};

export const buttonVariants = ({ variant = 'default', size = 'default', className }: ButtonVariantsProps = {}) =>
  clsx(baseStyles, variantStyles[variant], sizeStyles[size], className);

const Button: React.FC<ButtonProps> = ({
  children,
  type,
  disabled = false,
  onClick,
  variant = 'default',
  size = 'default',
  className,
  loading = false,
  loadingLabel,
  asChild = false,
  layoutId,
  ...rest
}) => {
  const prefersReducedMotion = useReducedMotion();
  const finalType = type ?? (variant === 'submit' ? 'submit' : 'button');
  const isDisabled = disabled || loading;
  const motionSafeProps = rest as unknown as Omit<HTMLMotionProps<'button'>, 'children'>;

  if (asChild && React.isValidElement(children)) {
    const child = children as React.ReactElement<{ className?: string }>;
    return React.cloneElement(child, {
      className: buttonVariants({ variant, size, className: clsx(child.props.className, className) }),
    });
  }

  return (
    <motion.button
      data-slot="button"
      disabled={isDisabled}
      aria-busy={loading || undefined}
      type={finalType}
      onClick={onClick}
      layoutId={layoutId}
      className={buttonVariants({ variant, size, className })}
      whileHover={isDisabled || prefersReducedMotion ? undefined : { y: -1, scale: 1.01 }}
      whileTap={isDisabled || prefersReducedMotion ? undefined : { y: 0, scale: 0.985 }}
      transition={prefersReducedMotion ? { duration: 0 } : dashboardTransitions.interaction}
      {...motionSafeProps}
    >
      <span className={clsx('inline-flex items-center gap-2', loading && 'opacity-0')}>{children}</span>
      {loading && (
        <span className="absolute inset-0 inline-flex items-center justify-center">
          <LoadingIndicator />
          <span className="sr-only">{loadingLabel ?? 'Loading'}</span>
        </span>
      )}
    </motion.button>
  );
};

export const PremiumButton = Button;
export default Button;
